/**
 * LevelCompleteScreen - Displayed when player completes a night/level
 * Features tabs for Upgrades and Progress (collectibles viewer)
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { UpgradeMenu } from './UpgradeMenu';
import { storyProgressionSystem } from '../../game/systems/StoryProgressionSystem';
import { loreItems, getLoreItemsForNight, type LoreItem } from '../../game/data/loreItems';
import type { RootState } from '../../state/store';

interface LevelCompleteScreenProps {
  nightNumber: number;
  onContinue: () => void;
  /** Number of collectibles collected this night (passed from gameplay) */
  collectiblesCollected?: number;
  /** Number of tasks/objectives completed this night (passed from gameplay) */
  tasksCompleted?: number;
  /** Total collectibles available */
  totalCollectibles?: number;
  /** Total tasks available */
  totalTasks?: number;
}

type TabType = 'summary' | 'upgrades' | 'progress';

export const LevelCompleteScreen: React.FC<LevelCompleteScreenProps> = ({
  nightNumber,
  onContinue,
  collectiblesCollected,
  tasksCompleted,
  totalCollectibles: totalCollectiblesProp,
  totalTasks: totalTasksProp,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [selectedItem, setSelectedItem] = useState<LoreItem | null>(null);
  const [progress, setProgress] = useState(storyProgressionSystem.getProgress());

  // Get objectives from Redux state (fallback)
  const objectives = useSelector((state: RootState) => state.game.objectives);
  const reduxCompletedTasks = objectives.filter(obj => obj.completed).length;
  const reduxCollectibles = progress.collectedItems.length;

  // Use props if provided, otherwise use Redux/system data
  const completedTasksCount = tasksCompleted ?? reduxCompletedTasks;
  const collectiblesCount = collectiblesCollected ?? reduxCollectibles;
  const totalCollectiblesAvailable = totalCollectiblesProp ?? progress.totalCollectibles;
  const totalTasksAvailable = totalTasksProp ?? objectives.length;

  // Calculate star rating based on tasks and collectibles
  // 1 star: No collectibles AND no tasks done (base completion)
  // 2 stars: At least 1 task done AND 6+ collectibles
  // 3 stars: 3+ tasks completed AND 18+ collectibles
  const calculateStars = (): number => {
    if (completedTasksCount >= 3 && collectiblesCount >= 18) {
      return 3;
    } else if (completedTasksCount >= 1 && collectiblesCount >= 6) {
      return 2;
    }
    return 1;
  };

  const starCount = calculateStars();

  useEffect(() => {
    // Update progress when component mounts
    setProgress(storyProgressionSystem.getProgress());
  }, []);

  const renderTabButton = (tab: TabType, label: string, color: string) => (
    <button
      onClick={() => setActiveTab(tab)}
      style={{
        padding: '12px 30px',
        fontSize: '18px',
        fontWeight: 'bold',
        fontFamily: 'monospace',
        backgroundColor: activeTab === tab ? color : '#1a1a1a',
        color: activeTab === tab ? '#000' : color,
        border: `3px solid ${color}`,
        borderBottom: activeTab === tab ? 'none' : `3px solid ${color}`,
        borderRadius: '8px 8px 0 0',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginRight: '5px',
      }}
    >
      {label}
    </button>
  );

  const renderSummaryTab = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px',
    }}>
      {/* Success message */}
      <div style={{
        fontSize: '72px',
        fontWeight: 'bold',
        color: '#00ff00',
        fontFamily: 'monospace',
        textShadow: '0 0 20px rgba(0, 255, 0, 0.8)',
        marginBottom: '30px',
        animation: 'pulse 2s infinite',
      }}>
        LEVEL COMPLETE!
      </div>

      {/* Night info */}
      <div style={{
        fontSize: '36px',
        color: '#ffffff',
        fontFamily: 'monospace',
        marginBottom: '30px',
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
      }}>
        Night {nightNumber} Survived
      </div>

      {/* Stars/rating */}
      <div style={{
        fontSize: '48px',
        marginBottom: '30px',
      }}>
        {'\u2B50'.repeat(starCount)}
      </div>

      {/* Star requirements hint */}
      <div style={{
        fontSize: '14px',
        color: '#666',
        fontFamily: 'monospace',
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        {starCount < 3 && (
          <span>
            {starCount === 1
              ? 'Complete 1+ task and collect 6+ items for 2 stars'
              : 'Complete 3+ tasks and collect 18+ items for 3 stars'}
          </span>
        )}
        {starCount === 3 && <span>Perfect rating achieved!</span>}
      </div>

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '40px',
        width: '100%',
        maxWidth: '800px',
      }}>
        <div style={statBoxStyle}>
          <div style={statLabelStyle}>Tasks Done</div>
          <div style={statValueStyle}>{completedTasksCount}/{totalTasksAvailable}</div>
        </div>
        <div style={statBoxStyle}>
          <div style={statLabelStyle}>Collectibles</div>
          <div style={statValueStyle}>{collectiblesCount}/{totalCollectiblesAvailable}</div>
        </div>
        <div style={statBoxStyle}>
          <div style={statLabelStyle}>Story Progress</div>
          <div style={statValueStyle}>{progress.storyPercentage}%</div>
        </div>
        <div style={statBoxStyle}>
          <div style={statLabelStyle}>Secrets Found</div>
          <div style={statValueStyle}>{progress.secretsFound}</div>
        </div>
      </div>

      {/* Continue button */}
      <button
        onClick={onContinue}
        style={{
          padding: '20px 60px',
          fontSize: '28px',
          fontWeight: 'bold',
          fontFamily: 'monospace',
          backgroundColor: '#2d7a4a',
          color: '#ffffff',
          border: '4px solid #00ff00',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0 0 20px rgba(0, 255, 0, 0.5)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#3a9959';
          e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 255, 0, 0.8)';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#2d7a4a';
          e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 255, 0, 0.5)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        CONTINUE
      </button>
    </div>
  );

  const renderProgressTab = () => {
    const allItems = getLoreItemsForNight(5);
    const collectedIds = new Set(progress.collectedItems.map(c => c.itemId));

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '20px',
      }}>
        {/* Progress Bar at Top */}
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: 'rgba(0, 170, 255, 0.1)',
          border: '2px solid #00aaff',
          borderRadius: '8px',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}>
            <span style={{ color: '#00aaff', fontFamily: 'monospace', fontSize: '18px' }}>
              STORY PROGRESS
            </span>
            <span style={{ color: '#ffffff', fontFamily: 'monospace', fontSize: '24px', fontWeight: 'bold' }}>
              {progress.storyPercentage}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '20px',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #333',
          }}>
            <div style={{
              width: `${progress.storyPercentage}%`,
              height: '100%',
              backgroundColor: '#00aaff',
              boxShadow: '0 0 10px rgba(0, 170, 255, 0.5)',
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '10px',
            color: '#888',
            fontFamily: 'monospace',
            fontSize: '14px',
          }}>
            <span>Collected: {progress.collectedItems.length}/{progress.totalCollectibles}</span>
            <span>Secrets: {progress.secretsFound}/3</span>
            <span>Nights: {progress.nightsCompleted}/7</span>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div style={{
          display: 'flex',
          flex: 1,
          gap: '20px',
          minHeight: 0,
        }}>
          {/* Left Panel - Collectibles List */}
          <div style={{
            width: '350px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            border: '2px solid #333',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#1a1a1a',
              borderBottom: '2px solid #333',
              color: '#00aaff',
              fontFamily: 'monospace',
              fontSize: '16px',
              fontWeight: 'bold',
            }}>
              COLLECTIBLES (Night 1-5)
            </div>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '10px',
            }}>
              {allItems.map(item => {
                const isCollected = collectedIds.has(item.id);
                const isSelected = selectedItem?.id === item.id;

                return (
                  <div
                    key={item.id}
                    onClick={() => isCollected && setSelectedItem(item)}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: isSelected ? 'rgba(0, 170, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: isSelected ? '2px solid #00aaff' : '2px solid transparent',
                      borderRadius: '6px',
                      cursor: isCollected ? 'pointer' : 'not-allowed',
                      opacity: isCollected ? 1 : 0.5,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                      <span style={{ fontSize: '24px' }}>
                        {isCollected ? item.icon : '?'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: isCollected ? '#ffffff' : '#666',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}>
                          {isCollected ? item.name : '???'}
                        </div>
                        <div style={{
                          color: '#888',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                        }}>
                          Night {item.nightFound} {item.isSecret && isCollected ? '(Secret)' : ''}
                        </div>
                      </div>
                      {isCollected && (
                        <span style={{ color: '#00ff00', fontSize: '16px' }}>
                          {'\u2713'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Collectible Viewer */}
          <div style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            border: '2px solid #333',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {selectedItem ? (
              <>
                {/* Header */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#1a1a1a',
                  borderBottom: '2px solid #333',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                  }}>
                    <span style={{ fontSize: '48px' }}>{selectedItem.icon}</span>
                    <div>
                      <div style={{
                        color: '#ffffff',
                        fontFamily: 'monospace',
                        fontSize: '24px',
                        fontWeight: 'bold',
                      }}>
                        {selectedItem.title}
                      </div>
                      <div style={{
                        color: '#888',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }}>
                        {selectedItem.dateInGame} | {selectedItem.location}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div style={{
                  flex: 1,
                  padding: '20px',
                  overflowY: 'auto',
                  backgroundColor: 'rgba(20, 15, 10, 0.8)',
                }}>
                  <div style={{
                    color: '#d4c4a8',
                    fontFamily: 'Georgia, serif',
                    fontSize: '16px',
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                  }}>
                    {selectedItem.content}
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  padding: '15px 20px',
                  backgroundColor: '#1a1a1a',
                  borderTop: '2px solid #333',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{
                    color: selectedItem.isSecret ? '#ff00ff' : '#00aaff',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                  }}>
                    {selectedItem.type.replace('_', ' ')} {selectedItem.isSecret ? '- SECRET' : ''}
                  </span>
                  <span style={{
                    color: '#666',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                  }}>
                    Found: Night {storyProgressionSystem.getCollectedItem(selectedItem.id)?.nightCollected || nightNumber}
                  </span>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#666',
                fontFamily: 'monospace',
              }}>
                <span style={{ fontSize: '64px', marginBottom: '20px' }}>{'\u{1F4DC}'}</span>
                <span style={{ fontSize: '18px' }}>Select a collectible to view</span>
                <span style={{ fontSize: '14px', marginTop: '10px', color: '#444' }}>
                  Locked items must be found in-game
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      animation: 'fadeIn 0.5s ease-in',
    }}>
      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '20px',
        borderBottom: '3px solid #333',
      }}>
        {renderTabButton('summary', 'SUMMARY', '#00ff00')}
        {renderTabButton('upgrades', 'UPGRADES', '#ffaa00')}
        {renderTabButton('progress', 'PROGRESS', '#00aaff')}
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
      }}>
        {activeTab === 'summary' && renderSummaryTab()}
        {activeTab === 'upgrades' && (
          <div style={{ padding: '20px', height: '100%' }}>
            <UpgradeMenu
              visible={true}
              onClose={() => setActiveTab('summary')}
              onPurchase={(upgradeId) => {
                console.log(`Purchased upgrade: ${upgradeId}`);
              }}
              embedded={true}
            />
          </div>
        )}
        {activeTab === 'progress' && renderProgressTab()}
      </div>

      {/* Keyframes for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            text-shadow: 0 0 20px rgba(0, 255, 0, 0.8);
          }
          50% {
            transform: scale(1.05);
            text-shadow: 0 0 40px rgba(0, 255, 0, 1);
          }
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        ::-webkit-scrollbar-thumb {
          background: #444;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

// Stat box styles
const statBoxStyle: React.CSSProperties = {
  padding: '20px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '2px solid #333',
  borderRadius: '8px',
  textAlign: 'center',
};

const statLabelStyle: React.CSSProperties = {
  color: '#888',
  fontFamily: 'monospace',
  fontSize: '12px',
  textTransform: 'uppercase',
  marginBottom: '5px',
};

const statValueStyle: React.CSSProperties = {
  color: '#ffffff',
  fontFamily: 'monospace',
  fontSize: '24px',
  fontWeight: 'bold',
};
